import React, { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ProcessRequestExecutionState from '@app/model/process-request/ProcessRequestExecutionState';
import ProcessRequestExecutionStateStatistics from '@app/model/process-request/ProcessRequestExecutionStateStatistics';
import ProcessRequestQueryKeys from '@app/model/process-request/service/ProcessRequestQueryKeys';
import ProcessRequestService from '@app/model/process-request/service/ProcessRequestService';
import {
	lookedUpState,
	pollAction,
} from '@app/site/secured/rateshopper/rates/provider/LatestProcessRequestExecutionStateMonitor.utilities';


export type LatestProcessRequestExecutionStateMonitorContextValue = {
	isActive: boolean,
	isInteractionLocked: boolean,
	isLoading: boolean,
	latestActiveExecutionState: ProcessRequestExecutionState | null,
	latestCompletedExecutionState: ProcessRequestExecutionState | null,
	setLatestActiveExecutionState: (state: ProcessRequestExecutionState | null) => void,
	refetchLatestActiveExecutionState: () => void,
}

export const LatestProcessRequestExecutionStateMonitorContext = React.createContext<LatestProcessRequestExecutionStateMonitorContextValue>({
	isActive: false,
	isInteractionLocked: false,
	isLoading: false,
	latestActiveExecutionState: null,
	latestCompletedExecutionState: null,
	setLatestActiveExecutionState: () => undefined,
	refetchLatestActiveExecutionState: () => undefined,
});

const isSameExecutionStateStatistics = (
	left: ProcessRequestExecutionStateStatistics,
	right: ProcessRequestExecutionStateStatistics
) => {
	return left.total === right.total &&
		left.succeeded === right.succeeded &&
		left.failed === right.failed &&
		left.isFinished === right.isFinished &&
		left.isEmpty === right.isEmpty;
};

// Structural comparator over ProcessRequestExecutionState (and, via
// isSameExecutionStateStatistics, ProcessRequestExecutionStateStatistics). When it
// returns true the poll result is dropped, so identical re-deliveries no longer
// re-arm the green "completed" flash — it fires exactly once per real transition.
// If a field is added to either model that should count as a state change, mirror
// it here or updates carrying only that field will be silently ignored.
const isSameExecutionState = (
	left: ProcessRequestExecutionState | null,
	right: ProcessRequestExecutionState | null
) => {
	if (left === right) return true;
	if (!left || !right) return false;
	return left.requestId === right.requestId &&
		left.process === right.process &&
		left.profileId === right.profileId &&
		left.companyId === right.companyId &&
		left.companyHotelId === right.companyHotelId &&
		left.state === right.state &&
		left.result === right.result &&
		isSameExecutionStateStatistics(left.statistics.taskStatistics, right.statistics.taskStatistics) &&
		isSameExecutionStateStatistics(left.statistics.triggerStatistics, right.statistics.triggerStatistics);
};

const LatestProcessRequestExecutionStateMonitorProvider: React.FunctionComponent<PropsWithChildren<{}>> = ({children}) => {
	const queryClient = useQueryClient();
	const [latestActiveExecutionState, setLatestActiveExecutionState] = useState<ProcessRequestExecutionState | null>(null);
	const [latestCompletedExecutionState, setLatestCompletedExecutionState] = useState<ProcessRequestExecutionState | null>(null);
	const latestCompletedExecutionStateTimer = useRef<number | undefined>(undefined);
	const lookupRequestId = useRef<number | undefined>(undefined);
	const shouldPollLatestActiveExecutionState = !!latestActiveExecutionState && !latestActiveExecutionState.isFinished;
	const isInteractionLocked = shouldPollLatestActiveExecutionState;

	const clearLatestCompletedExecutionState = useCallback(() => {
		if (latestCompletedExecutionStateTimer.current) {
			window.clearTimeout(latestCompletedExecutionStateTimer.current);
		}
		latestCompletedExecutionStateTimer.current = window.setTimeout(() => {
			setLatestCompletedExecutionState(null);
			latestCompletedExecutionStateTimer.current = undefined;
		}, 3000);
	}, []);

	const markExecutionStateCompleted = useCallback((state: ProcessRequestExecutionState) => {
		void queryClient.cancelQueries({ queryKey: ['pmsMetrics'] }).then(() => queryClient.invalidateQueries({ queryKey: ['pmsMetrics'] }));
		setLatestCompletedExecutionState(state);
		clearLatestCompletedExecutionState();
	}, [clearLatestCompletedExecutionState, queryClient]);

	const applyLatestActiveExecutionState = useCallback((nextState: (previousState: ProcessRequestExecutionState | null) => ProcessRequestExecutionState | null) => {
		setLatestActiveExecutionState(previousState => {
			const state = nextState(previousState);
			if (isSameExecutionState(previousState, state)) return previousState;

			if (!!state && state.isFinished) {
				markExecutionStateCompleted(state);
			} else if (!state && !!previousState && !previousState.isFinished) {
				markExecutionStateCompleted(previousState);
			}

			return state;
		});
	}, [markExecutionStateCompleted]);

	const updateLatestActiveExecutionState = useCallback((state: ProcessRequestExecutionState | null) => {
		applyLatestActiveExecutionState(() => state);
	}, [applyLatestActiveExecutionState]);

	// A finished request leaves the latest-active query after 2 seconds, so a poll after a hidden browser tab can miss
	// its end (ROO2-233). The running request is then read by id; the result applies only to that same request.
	const lookUpLatestActiveExecutionState = useCallback((requestId: number) => {
		lookupRequestId.current = requestId;
		void ProcessRequestService.findProcessRequestExecutionState(requestId)
			.catch(() => null)
			.then(result => {
				if (lookupRequestId.current === requestId) lookupRequestId.current = undefined;
				applyLatestActiveExecutionState(previousState => lookedUpState(previousState, requestId, result));
			});
	}, [applyLatestActiveExecutionState]);

	const latestActiveExecutionStateQuery = useQuery({
		queryKey: ProcessRequestQueryKeys.latestActiveExecutionState(),
		queryFn: ProcessRequestService.findLatestActiveProcessRequestExecutionState,
		refetchOnWindowFocus: true,
		refetchInterval: shouldPollLatestActiveExecutionState ? 1000 : false,
	});

	useEffect(() => {
		if (!latestActiveExecutionStateQuery.isSuccess) return;
		const polledState = latestActiveExecutionStateQuery.data ?? null;
		const action = pollAction(latestActiveExecutionState, polledState, lookupRequestId.current);
		if (action.type === 'apply') updateLatestActiveExecutionState(polledState);
		else if (action.type === 'lookUp') lookUpLatestActiveExecutionState(action.requestId);
	}, [latestActiveExecutionStateQuery.data, latestActiveExecutionStateQuery.dataUpdatedAt, latestActiveExecutionStateQuery.isSuccess]);

	useEffect(() => {
		return () => {
			if (latestCompletedExecutionStateTimer.current) {
				window.clearTimeout(latestCompletedExecutionStateTimer.current);
			}
		};
	}, []);

	const { isLoading, refetch } = latestActiveExecutionStateQuery;

	const contextValue = useMemo<LatestProcessRequestExecutionStateMonitorContextValue>(() => ({
		isActive: shouldPollLatestActiveExecutionState,
		isInteractionLocked: isInteractionLocked,
		isLoading: isLoading,
		latestActiveExecutionState: latestActiveExecutionState,
		latestCompletedExecutionState: latestCompletedExecutionState,
		setLatestActiveExecutionState: updateLatestActiveExecutionState,
		refetchLatestActiveExecutionState: refetch,
	}), [
		shouldPollLatestActiveExecutionState,
		isInteractionLocked,
		isLoading,
		latestActiveExecutionState,
		latestCompletedExecutionState,
		updateLatestActiveExecutionState,
		refetch,
	]);

	return <LatestProcessRequestExecutionStateMonitorContext.Provider value={contextValue}>
		<div className={isInteractionLocked ? 'contents pointer-events-none' : 'contents'}
		     inert={isInteractionLocked}
		     aria-busy={isInteractionLocked}>
			{children}
		</div>
	</LatestProcessRequestExecutionStateMonitorContext.Provider>;
};

export default LatestProcessRequestExecutionStateMonitorProvider;
